<script setup>
import { computed } from 'vue';
import { Button, Card } from 'ant-design-vue';
import { useBookingServiceSettings } from '@/composables/useBookingServiceSettings';
import { taiwanHolidayName } from '@/constants/taiwanHolidays';

const {
    canUpdate,
    loading,
    savingDates,
    month,
    openDates,
    monthDays,
    toggleDate,
    saveDates
} = useBookingServiceSettings({ mode: 'schedule' });

const weekdayLabels = ['日', '一', '二', '三', '四', '五', '六'];

const monthTitle = computed(() => {
    const [year, mon] = String(month.value || '').split('-');
    if (!year || !mon) return '';
    return `${year}年${Number(mon)}月`;
});

const todayIso = new Date().toISOString().slice(0, 10);

const weeks = computed(() => {
    const [year, mon] = String(month.value || '').split('-').map(Number);
    if (!year || !mon) return [];
    const firstWeekday = new Date(year, mon - 1, 1).getDay();
    const cells = Array.from({ length: firstWeekday }, () => null);
    monthDays.value.forEach((iso) => {
        const day = Number(iso.slice(-2));
        const weekday = new Date(year, mon - 1, day).getDay();
        cells.push({
            iso,
            day,
            weekend: weekday === 0 || weekday === 6,
            holiday: taiwanHolidayName(iso),
            open: openDates.value.includes(iso),
            today: iso === todayIso
        });
    });
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let index = 0; index < cells.length; index += 7) {
        rows.push(cells.slice(index, index + 7));
    }
    return rows;
});

const shiftMonth = (delta) => {
    const [year, mon] = String(month.value || '').split('-').map(Number);
    if (!year || !mon) return;
    const next = new Date(year, mon - 1 + delta, 1);
    const nextMonth = String(next.getMonth() + 1).padStart(2, '0');
    month.value = `${next.getFullYear()}-${nextMonth}`;
};
</script>

<template>
    <div class="cal-page pb-10">
        <Card :bordered="false" class="admin-panel" :loading="loading">
            <div class="cal-toolbar">
                <div class="cal-month">
                    <button type="button" aria-label="上個月" @click="shiftMonth(-1)">‹</button>
                    <strong>{{ monthTitle }}</strong>
                    <button type="button" aria-label="下個月" @click="shiftMonth(1)">›</button>
                </div>
                <Button v-if="canUpdate" type="primary" :loading="savingDates" @click="saveDates">
                    儲存這個月
                </Button>
            </div>

            <div class="cal-legend">
                <span><i class="dot dot--open" />可排班</span>
                <span><i class="dot dot--off" />不排</span>
                <span class="cal-legend__holiday">國定假日</span>
            </div>

            <div class="cal-grid" role="grid" aria-label="可排班日">
                <div
                    v-for="label in weekdayLabels"
                    :key="label"
                    class="cal-weekday"
                    :class="{ 'is-weekend': label === '日' || label === '六' }"
                >
                    {{ label }}
                </div>
                <template v-for="(week, weekIndex) in weeks" :key="weekIndex">
                    <button
                        v-for="(cell, dayIndex) in week"
                        :key="cell?.iso || `empty-${weekIndex}-${dayIndex}`"
                        type="button"
                        class="cal-day"
                        :class="{
                            'is-empty': !cell,
                            'is-open': cell?.open,
                            'is-weekend': cell?.weekend,
                            'is-holiday': Boolean(cell?.holiday),
                            'is-today': cell?.today
                        }"
                        :disabled="!cell || !canUpdate"
                        @click="cell && toggleDate(cell.iso, !cell.open)"
                    >
                        <template v-if="cell">
                            <span class="cal-day__num">{{ cell.day }}</span>
                            <span v-if="cell.open" class="cal-day__mark">可排</span>
                            <span v-if="cell.holiday" class="cal-day__holiday">{{ cell.holiday }}</span>
                        </template>
                    </button>
                </template>
            </div>
        </Card>
    </div>
</template>

<style scoped>
.cal-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.cal-page h2 {
    margin: 0 0 12px;
    font-size: 16px;
}
.cal-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
}
.cal-month {
    display: flex;
    align-items: center;
    gap: 8px;
}
.cal-month strong {
    min-width: 8rem;
    text-align: center;
    font-size: 18px;
}
.cal-month button {
    width: 32px;
    height: 32px;
    border: 1px solid #ebe4d6;
    border-radius: 8px;
    background: #fff;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
}
.cal-service {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b6458;
}
.cal-legend {
    display: flex;
    gap: 16px;
    margin: 16px 0 12px;
    color: #6b6458;
    font-size: 12px;
}
.cal-legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
}
.dot--open {
    background: #1f8a4c;
}
.dot--off {
    background: #fff;
    box-shadow: inset 0 0 0 1px #d9d3c7;
}
.cal-legend__holiday {
    color: #c45c4a;
    font-weight: 600;
}
.cal-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px;
}
.cal-weekday {
    padding: 6px 0 8px;
    text-align: center;
    font-size: 12px;
    color: #8a8378;
}
.cal-weekday.is-weekend {
    color: #c45c4a;
}
.cal-day {
    min-height: 78px;
    padding: 8px;
    border: 1px solid #efe8dc;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    cursor: pointer;
}
.cal-day.is-empty {
    border-color: transparent;
    background: transparent;
    cursor: default;
}
.cal-day.is-open:not(.is-empty) {
    background: #e5f6ec;
    border-color: #7dcea0;
    box-shadow: inset 4px 0 0 #1f8a4c;
}
.cal-day.is-today .cal-day__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #c4a35a;
    color: #fff;
}
.cal-day__num {
    font-size: 15px;
    font-weight: 600;
    color: #3f3a33;
}
.cal-day.is-weekend .cal-day__num {
    color: #c45c4a;
}
.cal-day__mark {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 999px;
    background: #1f8a4c;
    color: #fff;
    font-size: 11px;
    line-height: 18px;
    vertical-align: 1px;
}
.cal-day__holiday {
    display: block;
    margin-top: 6px;
    color: #c45c4a;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
}
.cal-day:disabled {
    cursor: default;
}
@media (max-width: 720px) {
    .cal-day {
        min-height: 64px;
        padding: 6px;
    }
    .cal-day__holiday {
        font-size: 10px;
    }
}
</style>
